import { useEffect, useState, useMemo } from "react";
import * as MUI from "@mui/material";
import yaml from "yaml";
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation, useParams } from "react-router-dom";

import RefreshIcon from '@mui/icons-material/Refresh';
import CodeIcon from '@mui/icons-material/Code';
import DeleteIcon from '@mui/icons-material/Delete';

import api from "../api";

function servicePriceString(service) {
    if (service.pay_amount === 0) {
        return "Включено в тариф";
    }

    var price = service.pay_amount + " ₽";

    if (service.one_time_service) {
        price += " единовременно";
    }

    if (service.pay_period_unit === "day") {
        if (service.pay_period_length > 1) {
            price += " за " + service.pay_period_length + " дн";
        } else {
            price += " в день";
        }
    }
    if (service.pay_period_unit === "month") {
        if (service.pay_period_length > 1) {
            price += " за " + service.pay_period_length + " мес";
        } else {
            price += " в месяц";
        }
    }

    return price;
}

function ServiceCard({service, subscribe, unsubscribe}) {
    return (<MUI.Grid item xs={12} lg={6} xl={4}>
        <MUI.Card>
            <MUI.CardContent>
                <MUI.Typography variant="h6">{service.name}</MUI.Typography>
                <MUI.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{servicePriceString(service)}</MUI.Typography>
                <MUI.Typography variant="body2" color="text.secondary">{service.description}</MUI.Typography>
            </MUI.CardContent>
            <MUI.CardActions sx={{ ml: 1, mb: 1, display: 'flex' }}>
                <MUI.Box sx={{ flex: 1 }}>
                {service.service_id ? (<MUI.Button
                        variant="outlined"
                        size="small"
                        color="success"
                    >Подключено</MUI.Button>) :
                    (<MUI.Button
                        variant="contained"
                        size="small"
                        onClick={() => subscribe(service)}
                        disabled={service.disabled}
                    >Подключить</MUI.Button>)
                }
                {service.tv_channel_count && (<MUI.Button size="small" color="info" sx={{ ml: 2 }}>
                    ТВ Каналов: {service.tv_channel_count}
                </MUI.Button>)}
                </MUI.Box>
                {service.service_id && (<MUI.IconButton
                        variant="contained"
                        size="small"
                        color="error"
                        onClick={() => unsubscribe(service)}
                    ><DeleteIcon /></MUI.IconButton>)
                }
            </MUI.CardActions>
        </MUI.Card>
    </MUI.Grid>);
}

function SubscribeDialog({subscriptionId, service}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(false);
    }, [service]);

    const doSubscribe = async () => {
        setLoading(true);
        try {
            await api.call('client/serviceSubscribe', {
                subscription_id: subscriptionId,
                tarif_id: service.tarif_id,
                offer_id: service.offer_id
            });
            navigate(-2);
        } catch (e) {

        } finally {
            setLoading(false);
        }
    }

    return (<>
        <MUI.Dialog open={location.search === '?subscribe'} onClose={() => navigate(-1)}>
            <MUI.DialogTitle>Подключение услуги</MUI.DialogTitle>
            <MUI.Divider />
            <MUI.DialogContent>
                <MUI.Typography variant="h6">{service.name}</MUI.Typography>
                <MUI.Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{servicePriceString(service)}</MUI.Typography>
                <MUI.Typography variant="body2" color="text.secondary">{service.description}</MUI.Typography>
                <MUI.Stack spacing={2}>
                </MUI.Stack>
            </MUI.DialogContent>
            <MUI.DialogActions>
                <MUI.Button onClick={doSubscribe} disabled={loading}>Подключить сейчас</MUI.Button>
                <MUI.Button onClick={() => navigate(-1)}>Отмена</MUI.Button>
            </MUI.DialogActions>
        </MUI.Dialog>
    </>);
}

function UnsubscribeDialog({subscriptionId, service}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(false);
    }, [service]);

    const doUnsubscribe = async () => {
        setLoading(true);
        try {
            await api.call('client/serviceUnsubscribe', {
                subscription_id: subscriptionId,
                service_id: service.service_id
            });
            navigate(-2);
        } catch (e) {

        } finally {
            setLoading(false);
        }
    }

    return (<>
        <MUI.Dialog open={location.search === '?unsubscribe'} onClose={() => navigate(-1)}>
            <MUI.DialogTitle>Отключение услуги</MUI.DialogTitle>
            <MUI.Divider />
            <MUI.DialogContent>
                <MUI.Typography variant="h6">{service.name}</MUI.Typography>
                <MUI.Stack spacing={2}>
                </MUI.Stack>
            </MUI.DialogContent>
            <MUI.DialogActions>
                <MUI.Button onClick={doUnsubscribe} disabled={loading}>Отключить сейчас</MUI.Button>
                <MUI.Button onClick={() => navigate(-1)}>Отмена</MUI.Button>
            </MUI.DialogActions>
        </MUI.Dialog>
    </>);
}

export default function AvailableServices() {
    const navigate = useNavigate();
    const subscriptionId = parseInt(useParams().id);
    const [services, setServices] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [debugMode, setDebugMode] = useState(false);
    let [serviceGroupId, setServiceGroupId] = useState('');
    const [currentService, setCurrentService] = useState({});

    const updateServices = async () => {
        setIsLoading(true);
        try {
            const servResp = await api.call('client/serviceList', {
                subscription_id: subscriptionId
            });
            setServices(servResp.services);
        } catch (error) {
            console.error("Ошибка при получении данных:", error);
        } finally {
            setIsLoading(false);
        }
    };

    const serviceGroups = useMemo(() => {
        return services.reduce((list, service) => {
            list[service.group_id] = service.group_name;
            return list;
        }, {});
    }, [services]);
    if (serviceGroupId === '') serviceGroupId = Object.keys(serviceGroups)[0] || '';

    const groupedServices = useMemo(() => {
        const filtered = services.filter((service) => service.group_id === serviceGroupId);
        const grouped = filtered.reduce((acc, service) => {
            const subgroup = service.subgroup || 'Другие услуги';
            if (!acc[subgroup]) {
                acc[subgroup] = [];
            }
            acc[subgroup].push(service);
            return acc;
        }, {});
        return grouped;
    }, [services, serviceGroupId]);

    useEffect(() => {
        updateServices();
    }, []);

    const serviceSubscribe = (service) => {
        setCurrentService(service);
        navigate('?subscribe');
    }

    const serviceUnubscribe = (service) => {
        setCurrentService(service);
        navigate('?unsubscribe');
    }

    const toggleDebugMode = () => {
        setDebugMode(!debugMode);
    };

    return (<>
        <Helmet>
            <title>Управление услугами</title>
        </Helmet>
        <MUI.Toolbar variant="dense">
            <MUI.Typography variant="h6" sx={{ flexGrow: 1 }}>
                Управление услугами
            </MUI.Typography>
            <MUI.Tooltip title="Обновить данные">
                <MUI.IconButton 
                    color="primary" 
                    onClick={updateServices}
                    disabled={isLoading}
                >
                    <RefreshIcon />
                </MUI.IconButton>
            </MUI.Tooltip>
            <MUI.Tooltip title={debugMode ? "Нормальный режим" : "Режим отладки"}>
                <MUI.IconButton 
                    color={debugMode ? "secondary" : "default"} 
                    onClick={toggleDebugMode}
                >
                    <CodeIcon />
                </MUI.IconButton>
            </MUI.Tooltip>
        </MUI.Toolbar>
        <MUI.Divider />
        
        <SubscribeDialog subscriptionId={subscriptionId} service={currentService} />
        <UnsubscribeDialog subscriptionId={subscriptionId} service={currentService} />

        {isLoading ? (
            <MUI.Box display="flex" alignItems="center" justifyContent="center" sx={{ height: '100%' }}>
                <MUI.CircularProgress />
            </MUI.Box>
        ) : (
            <MUI.Box sx={{ p: 2 }}>
                {debugMode ? (
                    <pre>{yaml.stringify(services)}</pre>
                ) : (<>
                    <MUI.Tabs
                        value={serviceGroupId}
                        onChange={(e, groupId) => setServiceGroupId(groupId)} sx={{ mb: 2 }}
                        variant="scrollable"
                        scrollButtons="auto"
                    >
                        {Object.keys(serviceGroups).map((id) => (
                            <MUI.Tab label={serviceGroups[id]} value={id} />
                        ))}
                    </MUI.Tabs>
                    {Object.keys(groupedServices).map((subgroup) => (
                        <MUI.Box key={subgroup} sx={{ mb: 4 }}>
                            <MUI.Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
                                {subgroup}
                            </MUI.Typography>
                            <MUI.Grid container spacing={2}>
                                {groupedServices[subgroup].map((service) => (
                                    <ServiceCard 
                                        key={service.offer_id+":"+service.tarif_id} 
                                        service={service} 
                                        subscribe={serviceSubscribe} 
                                        unsubscribe={serviceUnubscribe} 
                                    />
                                ))}
                            </MUI.Grid>
                        </MUI.Box>
                    ))}
                </>)}
            </MUI.Box>
        )}
    </>);
}
